import PropTypes from 'prop-types'
import { useRef, useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import Button from 'components/Button'
import useContent from 'store/content'
import { testKeyCodeAnswer } from 'libs/localApi'
import { FiEye } from 'react-icons/fi';
import { FiEyeOff } from 'react-icons/fi';

import {
  createCookieWithTimeParams,
  validateLockCookie,
  addBrToText,
  isLinkInternal,
  getRouterLink,
  setCurrentDomainToLink,
} from 'utils'

import {
  StyledSmallLabel,
  StyledForm,
  StyledInput,
  StyledLabel,
  StyledLoader,
  LockImageBefore,
  LockImageAfter,
  LockThumbnailImage,
  ScInputContainer,
  ScShowHideButton,
  StyledTitle
} from './styles'
import { useOnlineStatus } from '../../hooks/useOnlineStatus'
import PwaOfflinePlaceholder from 'components/PwaOfflinePlaceholder'
import { withUtm } from 'utils/utm'

const KNOWN_VALUE = ' * * * *'

const Lock = ({ part, themeInfos }) => {
  const online = useOnlineStatus()

  const [valid, setValid] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false);

  const content = useContent()
  const router = useRouter()
  const inputEl = useRef(null)

  const appId = content.get.appId()
  const lockInfos = content.get.lockById(part.id)

  useEffect(() => {
    const hasCookie = validateLockCookie(lockInfos)
    setValid(hasCookie)
    setSubmitted(hasCookie)
  }, [])

  if (!online) {
    return <PwaOfflinePlaceholder />
  }

  if (!lockInfos) {
    return null
  }

  const getIsInvalid = () => {
    const { onError } = lockInfos

    if (onError) {
      const isInvalid = submitted && !valid
      const hasMessage = onError.action === 'message' && onError.message.length > 0

      return isInvalid && hasMessage
    }

    return false
  }

  const redirect = (url) => {
    setLoading(true)
    setTimeout(() => {
      if (isLinkInternal(url)) {
        const { href, hardNavigation } = getRouterLink(url, router.query.appId, router.asPath)
        if (hardNavigation) {
          window.location.href = withUtm(href)
        } else {
          router.push(withUtm(href))
        }
      } else {
        window.location.href = withUtm(setCurrentDomainToLink(url))
      }
      setLoading(false)
    }, 750)
  }

  const handleOnSubmit = async (e) => {
    const { id, onSuccess, onError, unlockTime } = lockInfos

    // needs preventDefault to redirect
    e.preventDefault()

    // if COOKIE GO!
    const pass = inputEl.current.value

    const isRight = pass !== '' ? await testKeyCodeAnswer(appId, id, pass) : valid && submitted

    setValid(isRight)
    setSubmitted(true)

    if (isRight) {
      createCookieWithTimeParams(`lock-${id}`, unlockTime.value, unlockTime.unit)
      redirect(onSuccess.url)
    } else if (onError.action === 'redirect') {
      redirect(onError.url)
    }

    return false
  }

  const {
    onError: { message = '' } = {},
    display: {
      title = '',
      text = '',
      button = '',
      headerBanner = '',
      thumbnailImage = '',
      footerBanner = '',
      color = '',
    } = {},
    style: { borderRadius = '0' } = {}
  } = lockInfos

  return (
    <>
      {headerBanner !== '' && (
        <LockImageBefore>
          <img src={headerBanner} alt="lock" />
        </LockImageBefore>
      )}

      <StyledForm autoComplete="off" onSubmit={handleOnSubmit} $color={color}>
        {thumbnailImage !== '' && (
          <LockThumbnailImage $borderRadius={borderRadius}>
            <div>
              <img src={thumbnailImage} alt={title} />
            </div>
          </LockThumbnailImage>
        )}

        {title && title.length > 0 && <StyledTitle>{title}</StyledTitle>}

        {text && text.length > 0 && (
          <StyledLabel htmlFor={`lockpass-${part.id}`} style={{ marginBottom: '20px' }}>
            {addBrToText(text, 'lock-txt')}
          </StyledLabel>
        )}

        <ScInputContainer>
          <StyledInput
            ref={inputEl}
            name={`lockpass-${part.id}`}
            id={`lockpass-${part.id}`}
            placeholder={submitted && valid ? KNOWN_VALUE : ''}
            $error={submitted && !valid}
            $valid={submitted && valid}
            type={showPassword ? 'text' : 'password'}
            style={{ paddingRight: '40px' }}
          />
          {(!submitted || !valid) && (
            <ScShowHideButton
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? 'Cacher la réponse' : 'Afficher la réponse'}
              $hasIcons={submitted || valid || (submitted && !valid)}
            >
              {showPassword ? <FiEye /> : <FiEyeOff />}
            </ScShowHideButton>
          )}
        </ScInputContainer>

        {getIsInvalid() && <StyledSmallLabel $state="error">{message}</StyledSmallLabel>}

        <Button
          id="lock submit button"
          onClick={handleOnSubmit}
          disabled={loading}
          isLock
          themeInfos={themeInfos}
        >
          <>
            {loading && <StyledLoader />}
            {button}
          </>
        </Button>
      </StyledForm>

      {footerBanner !== '' && (
        <LockImageAfter>
          <img src={footerBanner} alt="lock" />
        </LockImageAfter>
      )}
    </>
  )
}

Lock.propTypes = {
  part: PropTypes.object,
  themeInfos: PropTypes.object,
  t: PropTypes.func,
}

export default Lock
